import { useWallet } from "@solana/wallet-adapter-react";
import * as token from "@solana/spl-token";
import { PublicKey, Transaction } from "@solana/web3.js";
import { TokenInfo } from "@solana/spl-token-registry";
import { useState, useCallback, useEffect } from "react";
import { Row, Col, Card, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDarknetIdentity } from "../../contexts/IdentityContext";
import { useDarknetSocket } from "../../contexts/SocketContext";
import { GameGenre } from "../../shared/enums";

import { formatNumber, getGameAssetsUrl, getProfilePicture, getTokenIcons } from "../../utilities/Helpers";
import ApiService from "../../services/ApiService";
import StorageService from "../../services/StorageService";
import WageredModal from "../modals/WageredModal";
import CasualModal from "../modals/CasualModal";

import player from "../../styles/images/profile.png";
import checkmark from "../../styles/images/checkmark.png";
import times from "../../styles/images/times.png";
import coin from "../../styles/images/coin.png";
import controller from "../../styles/images/controller.png";
import rightArrow from "../../styles/images/right-arrow.png";

const DEFAULT_ADMIN = process.env.REACT_APP_DEFAULT_ADMIN ?? "";

const OpenGamesWidgets = () => {
    const { publicKey, connected } = useWallet();
    const wallet = useWallet();
    const navigate = useNavigate();
    const { socket } = useDarknetSocket();
    const { authenticated, program, identity } = useDarknetIdentity();
    const [instances, setInstances] = useState<InstanceData[]>();
    const [tokenIcons, setTokenIcons] = useState<Map<string, TokenInfo>>();

    useEffect(() => {
        ApiService.get('/tokens').then((response) => {
            if (!response) throw new Error('No response');
            if (!response.success) console.log(response.message);
            getTokenIcons(response.data).then(setTokenIcons);
        }).catch((error: any) => {
            console.error(error);
        });
    }, []);

    const handleInstances = useCallback((response?: CallbackResponse) => {
        if (!response) throw new Error('No response');
        if (!response.success) console.log(response.message);
        const openGames = response.data as InstanceData[];
        setInstances(openGames);
    }, []);

    useEffect(() => {
        socket.emit('openGames', handleInstances);

        socket.on('openGames', setInstances);

        return () => {
            socket.off('openGames', setInstances);
        }
    }, [socket, handleInstances, setInstances]);

    const WageredGames = () => {
        if (!instances) return <li key="loading">Loading games...</li>;
        let wageredGames = instances.filter((i) => i.type === GameGenre.Wagered);

        if (wageredGames.length === 0)
            return <li key="empty">No available games</li>;

        let games = wageredGames.map((game) => {
            const challengable =
                program &&
                authenticated &&
                game.player1.address !== identity.account.address &&
                (game.status === "Preparing" ||
                    (game.status === "Waiting" &&
                        game.player2.address.length === 0));
            const wagerAmount = Number(game.wagerAmount);

            async function handleChallengeBtn() {
                if (!challengable) return;

                const wagerKey = new PublicKey(game.wagerAccount);
                const currentWager = await program.account.wager.fetch(wagerKey);

                console.log("creating wagered game");
                console.log("program", program.programId.toString());
                // @ts-ignore
                console.log("totalWagerCost", currentWager.buyIn);

                // Darknet default admin
                const admin = new PublicKey(DEFAULT_ADMIN);

                const [wagerVault] = PublicKey.findProgramAddressSync(
                    [wagerKey.toBuffer()],
                    program.programId
                );

                const [player] = PublicKey.findProgramAddressSync(
                    [wagerKey.toBuffer(), publicKey!.toBuffer()],
                    program.programId
                );

                try {
                    const joinWagerTx = new Transaction();
                    if (game.wagerToken.symbol === "SOL") {
                        // wager options = spl: bool, buy_in: u64, max_players: u8, text: String,
                        const joinWagerIx = await program.methods
                            .joinWager()
                            .accounts({
                                admin: admin,
                                wager: wagerKey,
                                wagerVault: wagerVault,
                                player: player,
                                authority: publicKey!,
                                // @ts-ignore
                                token: null,
                                // @ts-ignore
                                authorityTokenAccount: null,
                                // @ts-ignore
                                wagerVaultTokenAccount: null,
                                // @ts-ignore
                                tokenProgram: null,
                                // @ts-ignore
                                associatedTokenProgram: null,
                            })
                            .instruction();
                        joinWagerTx.add(joinWagerIx);
                    } else {
                        const tokenMint = new PublicKey(game.wagerToken.address);
                        const authorityTokenAccount = await token.getAssociatedTokenAddress(
                            tokenMint,
                            publicKey!,
                            true
                        );
                        const wagerVaultTokenAccount =
                            await token.getAssociatedTokenAddress(
                                tokenMint,
                                wagerVault,
                                true
                            );
                        const joinWagerIx = await program.methods
                            .joinWager()
                            .accounts({
                                admin: admin,
                                wager: wagerKey,
                                wagerVault: wagerVault,
                                player: player,
                                authority: publicKey!,
                                // @ts-ignore
                                token: tokenMint,
                                // @ts-ignore
                                authorityTokenAccount: authorityTokenAccount,
                                // @ts-ignore
                                wagerVaultTokenAccount: wagerVaultTokenAccount,
                                // @ts-ignore
                                tokenProgram: token.TOKEN_PROGRAM_ID,
                                // @ts-ignore
                                associatedTokenProgram: token.ASSOCIATED_TOKEN_PROGRAM_ID,
                            })
                            .instruction();
                        joinWagerTx.add(joinWagerIx);
                    }
                    joinWagerTx.feePayer = publicKey!;

                    const latestBlockHash =
                        await program.provider.connection.getLatestBlockhash();
                    joinWagerTx.recentBlockhash = latestBlockHash.blockhash;
                    const sig = await wallet.sendTransaction(
                        joinWagerTx,
                        program.provider.connection
                    );
                    const tx = await program.provider.connection.confirmTransaction({
                        blockhash: latestBlockHash.blockhash,
                        lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
                        signature: sig,
                    });
                    console.log("confirmed tx", tx);

                    if (!tx.value.err) {
                        let response = await ApiService.post("/challenge", {
                            instanceId: game.id,
                            wagered: true,
                        });
                        if (response && response.success) {
                            StorageService.set("authToken", response.data.token);
                            navigate(`/pong/${game.id}`);
                        } else {
                            console.error(response.message);
                            window.alert(response.message);
                        }
                    } else {
                        console.log("error", tx.value.err);
                        window.alert(tx.value.err);
                    }
                } catch (err: any) {
                    console.error(err);
                    window.alert(err.message);
                }
            }

            function handleSpectateBtn() {
                navigate(`/pong/${game.id}`);
            }

            return (
                <li className="open-game mb-1 bb-1 pb-1" key={game.id}>
                    <Row className="text-center">
                        <Col xs={2}>
                            <GameTooltip id={1} name={"pong"} />
                        </Col>
                        <Col xs={3}>
                            <PlayerTooltip player={game.player1} />
                        </Col>
                        <Col xs={2}>
                            {(game.player2.address.length > 0) ? "2/2" : "1/2"}
                        </Col>
                        <Col xs={2}>
                            {formatNumber(Number(wagerAmount.toFixed(3)))} <TokenTooltip token={game.wagerToken} />
                        </Col>
                        <Col xs={3}>
                            {(authenticated && connected &&
                                game.player1.address !== publicKey?.toBase58() &&
                                !game.player2.address && game.player2.address.length === 0) && (
                                    <button
                                        className="dg-btn dg-btn-primary py-1 px-2 w-50 text-light fs-7"
                                        onClick={handleChallengeBtn}
                                    >
                                        Accept
                                    </button>
                                )}
                            <button className="dg-btn dg-btn-secondary py-1 px-2 w-50 text-light fs-7" onClick={handleSpectateBtn}>
                                Watch
                            </button>
                        </Col>
                    </Row>
                </li>
            );
        });

        return <>{games}</>;
    }

    const CasualGames = () => {
        if (!instances) return <li key="loading">Loading games...</li>;
        let casualGames = instances.filter((i) => i.type === GameGenre.Casual);

        if (casualGames.length === 0)
            return <li key="empty">No available games</li>;

        let games = casualGames.map((game) => {
            async function handleChallengeBtn() {
                if (!authenticated) return;
                let response = await ApiService.post("/challenge", {
                    instanceId: game.id,
                });
                if (response && response.success) {
                    StorageService.set("authToken", response.data.token);
                    navigate(`/pong/${game.id}`);
                } else {
                    window.alert(response.message);
                }
            }

            function handleSpectateBtn() {
                navigate(`/pong/${game.id}`);
            }

            return (
                <li className="open-game mb-1 bb-1 pb-1" key={game.id}>
                    <Row className="text-center">
                        <Col xs={2}>
                            <GameTooltip id={1} name={"Pong"} />
                        </Col>
                        <Col xs={3}>
                            <button className="dg-btn dg-btn-link" onClick={() => navigate(`/profile/${game.player1.address}`)}>{game.player1.name}</button>
                        </Col>
                        <Col xs={2} className="px-0">
                            <img src={checkmark} alt="checkmark" /> {game.player1.wins}
                            &nbsp;
                            <img src={times} alt="times" /> {game.player1.losses}
                        </Col>
                        <Col xs={2}>
                            {(game.player2.address.length > 0) ? "2/2" : "1/2"}
                        </Col>
                        <Col xs={3}>
                            {(authenticated && connected &&
                                game.player1.address !== publicKey?.toBase58() &&
                                !game.player2.address && game.player2.address.length === 0) && (
                                    <button
                                        className="dg-btn dg-btn-primary py-1 px-2 w-50 text-light fs-7"
                                        onClick={handleChallengeBtn}
                                    >
                                        Accept
                                    </button>
                                )}
                            <button className="dg-btn dg-btn-secondary py-1 px-2 w-50 text-light fs-7" onClick={handleSpectateBtn}>
                                Watch
                            </button>
                        </Col>
                    </Row>
                </li>
            );
        });

        return <>{games}</>;
    }

    const AllGames = () => {
        if (!instances) return <li key="loading">Loading games...</li>;
        if (instances.length === 0) return <li key="empty">No available games</li>;

        let games = instances.map((game) => {
            function handleSpectateBtn() {
                navigate(`/pong/${game.id}`);
            }

            return (
                <li className="open-game mb-1 bb-1 pb-1" key={game.id}>
                    <Row className="text-center">
                        <Col xs={3}>
                            <img src={`${getGameAssetsUrl(1)}/icon.png`} height={32} alt={"Pong"} />
                        </Col>
                        <Col xs={4}>
                            <button className="dg-btn dg-btn-link" onClick={() => navigate(`/profile/${game.player1.address}`)}>{game.player1.name}</button>
                        </Col>
                        <Col xs={2}>
                            {game.type === GameGenre.Casual ? <img src={controller} alt="Casual" height={20} /> : <img src={coin} alt="Wagered" height={20} />}
                        </Col>
                        <Col xs={3}>
                            <button className="dg-btn dg-btn-secondary py-1 px-2 text-light fs-7 w-100" onClick={handleSpectateBtn}>
                                Watch
                            </button>
                        </Col>
                    </Row>
                </li>
            );
        });

        return <>{games}</>;
    }

    const GameTooltip = ({ id, name }: { id: number, name: string }) => {
        return (
            <OverlayTrigger
                placement="right"
                overlay={<Tooltip>{name}</Tooltip>}
            >
                <img src={`${getGameAssetsUrl(id)}/icon.png`} height={32} alt={name} />
            </OverlayTrigger>
        )
    }

    const PlayerTooltip = ({ player }: { player: Profile }) => {
        return (
            <OverlayTrigger
                placement="right"
                overlay={<Tooltip>
                    <img src={checkmark} alt="checkmark" /> {player.wins}
                    &nbsp;
                    <img src={times} alt="times" /> {player.losses}
                </Tooltip>}
            >
                <>
                    <img
                        src={getProfilePicture(player.address)}
                        alt="pfp"
                        height="30"
                    />
                    &nbsp;
                    <button className="dg-btn dg-btn-link" onClick={() => navigate(`/profile/${player.address}`)}>{player.name}</button>
                </>
            </OverlayTrigger>
        )
    }

    const TokenTooltip = ({ token }: { token: Token }) => {
        let icon = tokenIcons?.get(token.address)?.logoURI ?? tokenIcons?.get(token.testnet)?.logoURI;
        if (!icon || icon.length === 0) return <></>;

        return (
            <OverlayTrigger
                placement="right"
                overlay={<Tooltip>{token?.symbol}</Tooltip>}
            >
                <img src={icon} height={16} alt={token?.symbol} />
            </OverlayTrigger>
        )
    }

    return <>
        <Row className="d-none d-md-flex">
            <Col>
                <Card className="card-widget">
                    <Card.Header>
                        <Row className="justify-content-between">
                            <Col>
                                <h4>
                                    <img src={coin} alt="Wagered" height={20} /> Wagered Games
                                </h4>
                            </Col>
                            {authenticated && (
                                <Col className="text-end">
                                    <WageredModal />
                                </Col>
                            )}
                        </Row>
                    </Card.Header>
                    <Card.Body>
                        <div className="text-grey bb-2 text-center fs-7 mb-1">
                            <Row>
                                <Col xs={2}>Game</Col>
                                <Col xs={3}>Host</Col>
                                <Col xs={2}>Players</Col>
                                <Col xs={2}>Wager</Col>
                                <Col xs={3}>Actions</Col>
                            </Row>
                        </div>
                        <ul>
                            <WageredGames />
                        </ul>
                    </Card.Body>
                </Card>
            </Col>
            <Col>
                <Card className="card-widget">
                    <Card.Header>
                        <Row className="justify-content-between">
                            <Col>
                                <h4>
                                    <img src={controller} alt="Casual" height={20} /> Casual Games
                                </h4>
                            </Col>
                            {authenticated && (
                                <Col className="text-end">
                                    <CasualModal />
                                </Col>
                            )}
                        </Row>
                    </Card.Header>
                    <Card.Body>
                        <div className="text-grey bb-2 text-center fs-7 mb-1">
                            <Row>
                                <Col xs={2}>Game</Col>
                                <Col xs={3}>Host</Col>
                                <Col xs={2}>Stats</Col>
                                <Col xs={2}>Players</Col>
                                <Col xs={3}>Actions</Col>
                            </Row>
                        </div>
                        <ul>
                            <CasualGames />
                        </ul>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
        <Row className="d-flex d-md-none">
            <Col>
                <Card className="card-widget">
                    <Card.Header>
                        <Row className="justify-content-between">
                            <Col>
                                <h4>
                                    Open Games
                                </h4>
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body>
                        <div className="text-grey bb-2 text-center fs-8 mb-1 pb-2">
                            <Row>
                                <Col xs={3}>
                                    Game
                                </Col>
                                <Col xs={3} className="text-end">
                                    Host
                                </Col>
                                <Col xs={3} className="text-end">
                                    Wagered
                                </Col>
                                <Col xs={3}>
                                    Actions
                                </Col>
                            </Row>
                        </div>
                        <ul>
                            <AllGames />
                        </ul>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    </>
}

export default OpenGamesWidgets;